<template>
  <!-- ຕັ້ງຄ່າເງື່ອນໄຂ COD ລ່ວງໜ້າ (owner, 2026-10-26): the boss's ceilings for
       EVERY shop, new and old. ຜູ້ບໍລິຫານ / SuperAdmin set them here; Finance fits
       each shop inside them on the ຮ້ານຄ້າ page and only reads this one. -->
  <div class="container list-page cod-policy">
    <div class="list-head">
      <h2 class="list-title">
        <i class="fal fa-sliders-h"></i>
        {{ $t("codAdvance.policy_title") }}
      </h2>
    </div>

    <p class="list-state is-loading" v-if="isLoading">{{ $t("codAdvance.loading") }}</p>
    <p class="list-state is-error" v-else-if="loadError">{{ loadError }}</p>

    <template v-else-if="current">
      <div class="policy-card">
        <p class="fld-hint" v-if="current.source !== 'table'">
          {{ $t("codAdvance.policy_defaults_hint") }}
        </p>
        <p class="list-state is-error" v-if="current.available === false">
          {{ $t("codAdvance.policy_unavailable") }}
        </p>

        <div class="two-col">
          <div class="fld">
            <label class="fld-label" for="cod-policy-rate">{{ $t("codAdvance.max_rate_pct") }}</label>
            <div class="suffixed">
              <input id="cod-policy-rate" class="fld-input" type="number" min="1" max="100" step="0.01"
                     v-model.number="form.max_rate_pct" :disabled="!canEdit">
              <span class="suffix">%</span>
            </div>
            <p class="fld-hint">{{ $t("codAdvance.max_rate_pct_hint") }}</p>
          </div>
          <div class="fld">
            <label class="fld-label" for="cod-policy-credit">{{ $t("codAdvance.max_credit_limit_per_shop") }}</label>
            <KipInput id="cod-policy-credit" v-model="form.max_credit_limit" :disabled="!canEdit"
                      :placeholder="$t('codAdvance.blank_unlimited')"/>
            <p class="fld-hint">{{ $t("codAdvance.max_credit_limit_hint") }}</p>
          </div>
        </div>

        <div class="two-col">
          <div class="fld">
            <label class="fld-label" for="cod-policy-fee">{{ $t("codAdvance.max_fee_pct") }}</label>
            <div class="suffixed">
              <input id="cod-policy-fee" class="fld-input" type="number" min="0" max="50" step="0.01"
                     v-model.number="form.max_fee_pct" :disabled="!canEdit">
              <span class="suffix">%</span>
            </div>
          </div>
          <div class="fld">
            <label class="fld-label" for="cod-policy-min-fee">{{ $t("codAdvance.min_fee") }}</label>
            <KipInput id="cod-policy-min-fee" v-model="form.min_fee" :disabled="!canEdit"/>
            <p class="fld-hint">{{ $t("codAdvance.min_fee_hint") }}</p>
          </div>
        </div>

        <div class="two-col">
          <div class="fld">
            <label class="fld-label" for="cod-policy-min">{{ $t("codAdvance.min_advance_per_request") }}</label>
            <KipInput id="cod-policy-min" v-model="form.min_advance" :disabled="!canEdit"/>
          </div>
          <div class="fld" v-if="canEdit">
            <label class="fld-label" for="cod-policy-note">{{ $t("codAdvance.note") }}</label>
            <input id="cod-policy-note" class="fld-input" type="text" maxlength="500" v-model="form.note"
                   :placeholder="$t('codAdvance.note_placeholder')">
          </div>
        </div>

        <!-- What the minimums mean for one request, so a typo shows before it is saved. -->
        <div class="info-box example" v-if="exampleFee !== null">
          <div class="info-row">
            <span>{{ $t("codAdvance.example_withdraw", { amount: formatKip(form.min_advance) }) }}</span>
            <b>{{ $t("codAdvance.example_fee_net", { fee: formatKip(exampleFee), net: formatKip(form.min_advance - exampleFee) }) }}</b>
          </div>
        </div>

        <p class="fld-hint" v-if="!canEdit">{{ $t("codAdvance.policy_read_only") }}</p>
        <p class="list-state is-error" v-if="saveError">{{ saveError }}</p>
        <p class="list-state is-ok" v-if="saved">{{ $t("codAdvance.policy_saved") }}</p>
        <div class="form-foot" v-if="canEdit">
          <button type="button" class="cod-btn" :disabled="saving || !canSave" @click="save">
            {{ saving ? $t("codAdvance.saving") : $t("codAdvance.save_policy") }}
          </button>
        </div>
      </div>

      <div class="table-card" v-if="history.length">
        <h3 class="section-title">{{ $t("codAdvance.change_history") }}</h3>
        <vs-table :data="history">
          <template #thead>
            <vs-tr>
              <vs-th>{{ $t("codAdvanceReport.date") }}</vs-th>
              <vs-th>{{ $t("codAdvance.max_rate_pct_short") }}</vs-th>
              <vs-th>{{ $t("codAdvance.max_fee_pct") }}</vs-th>
              <vs-th>{{ $t("codAdvance.max_credit_limit") }}</vs-th>
              <vs-th>{{ $t("codAdvance.min_advance") }}</vs-th>
              <vs-th>{{ $t("codAdvance.min_fee") }}</vs-th>
              <vs-th>{{ $t("codAdvance.by") }}</vs-th>
              <vs-th>{{ $t("codAdvance.note") }}</vs-th>
            </vs-tr>
          </template>
          <template #tbody>
            <vs-tr :key="row.id" v-for="row in history">
              <vs-td>{{ formatDateTime(row.created_at) }}</vs-td>
              <vs-td>{{ formatPct(row.max_rate_pct) }}</vs-td>
              <vs-td>{{ formatPct(row.max_fee_pct) }}</vs-td>
              <vs-td>{{ row.max_credit_limit === null ? $t("codAdvance.unlimited") : formatKip(row.max_credit_limit) }}</vs-td>
              <vs-td>{{ formatKip(row.min_advance) }}</vs-td>
              <vs-td>{{ formatKip(row.min_fee) }}</vs-td>
              <vs-td>{{ row.created_by ? row.created_by.name : '—' }}</vs-td>
              <vs-td>{{ row.note || '—' }}</vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </div>
    </template>
  </div>
</template>

<script>
import KipInput from '@/views/Accounting/Customer/CodAdvance/components/KipInput';
import {errorMessage, fetchPolicy, formatDateTime, formatKip, formatPct, savePolicy} from '@/services/codAdvanceService';

export default {
  name: 'CodAdvancePolicy',
  components: {KipInput},
  data() {
    return {
      current: null,
      history: [],
      form: {max_rate_pct: null, max_fee_pct: null, max_credit_limit: null, min_advance: null, min_fee: null, note: ''},
      isLoading: false,
      loadError: null,
      saving: false,
      saveError: null,
      saved: false,
    };
  },
  computed: {
    // The approver roles (cod_advance.approver_roles); the server is the boundary.
    canEdit() {
      return this.$can(['SuperAdmin', 'manager']);
    },
    canSave() {
      const f = this.form;
      return f.max_rate_pct > 0 && f.max_fee_pct !== null && f.max_fee_pct !== '' && f.min_advance > 0
          && f.min_fee !== null && f.min_fee % 1000 === 0 && f.min_fee < f.min_advance;
    },
    // The server's rule: the percent rounded up to 1,000, never below the minimum fee.
    exampleFee() {
      const f = this.form;
      if (!(f.min_advance > 0) || f.min_fee === null || f.max_fee_pct === null || f.max_fee_pct === '') return null;
      const pct = Math.ceil((f.min_advance * Number(f.max_fee_pct)) / 100 / 1000) * 1000;
      return Math.max(Math.ceil(f.min_fee / 1000) * 1000, pct);
    },
  },
  created() {
    this.load();
  },
  methods: {
    formatDateTime,
    formatKip,
    formatPct,
    fill(current) {
      this.current = current;
      this.form = {
        max_rate_pct: Number(current.max_rate_pct),
        max_fee_pct: Number(current.max_fee_pct),
        max_credit_limit: current.max_credit_limit === null ? null : Number(current.max_credit_limit),
        min_advance: Number(current.min_advance),
        min_fee: Number(current.min_fee),
        note: '',
      };
    },
    async load() {
      this.isLoading = true;
      this.loadError = null;
      try {
        const data = await fetchPolicy();
        this.fill(data.current);
        this.history = data.history || [];
      } catch (e) {
        this.loadError = errorMessage(e, this.$t('codAdvance.load_failed'));
      } finally {
        this.isLoading = false;
      }
    },
    async save() {
      if (this.saving || !this.canSave) return;
      this.saving = true;
      this.saveError = null;
      this.saved = false;
      try {
        const data = await savePolicy({
          max_rate_pct: this.form.max_rate_pct,
          max_fee_pct: this.form.max_fee_pct,
          max_credit_limit: this.form.max_credit_limit,
          min_advance: this.form.min_advance,
          min_fee: this.form.min_fee,
          note: this.form.note || undefined,
        });
        this.fill(data.current);
        this.history = data.history || [];
        this.saved = true;
      } catch (e) {
        this.saveError = errorMessage(e, this.$t('codAdvance.save_failed'));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './components/cod-advance-ui';

.policy-card {
  background: #fff;
  border: 1px solid $cod-line;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 16px;
  text-align: left;
  max-width: 820px;
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
}

.example {
  margin: 4px 0 14px;
}

.form-foot {
  display: flex;
  justify-content: flex-end;
}

.section-title {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 700;
  text-align: left;
}

.list-state.is-ok {
  color: #1e8e4e;
}
</style>
